import type { CuriosityParams } from '@/app/components/search/Curiosity';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { APIStatusEnum } from '@/app/components/search/common/reloadData/APIStatusEnum.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import { type SearchInfoData, type SearchInfoDetailData } from '@/app/search/types.ts';
import { hasAllMandatoryParams } from '@/app/components/search/common/urlValidator.ts';
import { analyticsResultsEvent } from '@/app/components/search/Analytics/analyticsResultsEvent.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import type { DefaultDestItems } from '@/app/components/search/utils';
import type { StarTrack } from '@/app/common/starTrack/startrack.ts';
import { Context, Platform } from '@/app/common/starTrack/events/utils/starTrack.types.ts';
import { searchServedEvents } from '@/app/common/starTrack/events/utils/searchServedUtils.ts';
import {
  shouldTrackAdditionalParams,
  createSearchAdditionalParameterPayload,
} from '@/app/common/starTrack/events/searchAdditionalParameter.ts';
import { createSearchServedPayload } from '@/app/common/starTrack/events/searchServed.ts';
import { createResultPageFilterNoResultPayload } from '@/app/common/starTrack/events/resultPageFilterNoResult.ts';
import {
  createMapOutsideDestinationHotelsRetrievedPayload,
  hasOutsideDestinations,
} from '@/app/common/starTrack/events/mapOutsideDestinationHotelsRetrieved.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { initBiffSearchPage } from '@/app/common/biff/searchPage/biffSearchPage.ts';
import { BIFF_CONFIGURATIONS } from '@/app/common/biff/searchPage/typesSearchPage.ts';
import {
  analyticsViewItemListEvent,
  updateAnalyticsViewItemListEvent,
} from '@/app/components/search/Analytics/analyticsViewItemListEvent.ts';
import type { ListViewType } from '@/app/common/viewModeUtils.ts';
import { getPageTypeFromSearchParams } from '@/app/common/urlParametersUtils';
import { PAGE_TYPE } from '@/app/common/pageType';
import { trackingSearchRequested } from '@/app/common/tracking/trackingSearchRequested.ts';
import { trackingResultShown } from '@/app/common/tracking/trackingResultShown.ts';

type FireSearchProductsEventsBeforeProps = {
  searchParams: SearchParams;
  localization: Localization;
  commonData: CommonData;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  starTrack: StarTrack;
};
export const fireSearchProductsEventsBefore = ({
  searchParams,
  localization,
  commonData,
  searchInfoData,
  searchInfoDetailData,
  starTrack,
}: FireSearchProductsEventsBeforeProps) => {
  const { searchId } = searchInfoData;
  trackingSearchRequested({
    starTrack,
    searchId,
    searchParams,
    searchInfoData,
    searchInfoDetailData,
    commonData,
    localization,
    context: Context.listResults,
    platform: Platform.tsx,
    logText: '555 fireSearchProductsEventsBefore',
    apexPerimeterFlag: localization.configValuesApp.apexEnabledForResults,
  });
};

type FireSearchDestinationsEventsBeforeProps = {
  searchParams: SearchParams;
  localization: Localization;
  commonData: CommonData;
  curiosityParams: CuriosityParams;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  starTrack: StarTrack;
};
export const fireSearchDestinationsEventsBefore = ({
  searchParams,
  localization,
  commonData,
  searchInfoData,
  searchInfoDetailData,
  starTrack,
}: FireSearchDestinationsEventsBeforeProps) => {
  let context = Context.hp;
  if (Object.keys(searchParams).length !== 0 && hasAllMandatoryParams(searchParams)) {
    context = Context.destinationResults;
  }

  trackingSearchRequested({
    starTrack,
    searchId: searchInfoData.searchId,
    searchParams,
    searchInfoData,
    searchInfoDetailData,
    commonData,
    localization,
    context,
    platform: Platform.tsx,
    logText: '666 fireSearchDestinationsEventsBefore',
    apexPerimeterFlag: localization.configValuesApp.apexEnabledForResults,
  });
};

type FireSearchProductsEventsAfterProps = {
  isNewSearch: boolean;
  searchParams: SearchParams;
  productsData: ProductsData;
  listView: ListViewType;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  defaultDestItems: DefaultDestItems[];
  commonData: CommonData;
  localization: Localization;
  curiosityParams: CuriosityParams;
  starTrack: StarTrack;
};

export const fireSearchProductsEventsAfter = ({
  isNewSearch,
  searchParams,
  productsData,
  listView,
  searchInfoData,
  searchInfoDetailData,
  defaultDestItems,
  commonData,
  localization,
  starTrack,
}: FireSearchProductsEventsAfterProps) => {
  const { apiStatus } = productsData;
  const { searchId } = searchInfoData;
  const { authResponse } = commonData;

  try {
    if (isNewSearch) {
      if (shouldTrackAdditionalParams(searchParams)) {
        starTrack.searchAdditionalParameter(createSearchAdditionalParameterPayload({ searchId, searchParams }));
      }

      if (apiStatus !== APIStatusEnum.WAIT) {
        searchServedEvents({
          searchId,
          searchParams,
          productsData,
          localization,
          searchInfoData,
          starTrack,
        });

        const pageType = getPageTypeFromSearchParams(searchParams);

        // Products search is also triggered in deeplinks to hotel detail pages.
        // In that case we don't want to track analytics results event and Biff is initialized in hotel detail page.
        if (pageType === PAGE_TYPE.SEARCH_DESTINATIONS || pageType === PAGE_TYPE.SEARCH) {
          // Read before the event below pushes this page's visit entry, so a previous page's visit of the
          // same name cannot satisfy the wait biff's load() makes on it.
          const platformDataIndex = canUseDOM ? (window.platformData?.length ?? 0) : 0;

          analyticsResultsEvent({
            localization,
            totalNumberOfResults: productsData.productCounters?.productsCount || null,
            searchId: searchInfoData.searchId,
            defaultDestItems,
            dealCards: productsData?.dealCards,
            isSearch: true,
            searchInfoData,
            searchInfoDetailData,
            commonData,
          }).then(() => {
            analyticsViewItemListEvent({
              searchParams,
              localization,
              dealCards: productsData?.dealCards,
            });

            // After the visit event above is in platformData: biff builds its request from it. This function
            // only runs for products searches (searchInfoData.searchType), which always render the results
            // page, so the RESULTS configuration is not gated on the URL pageType: a stale
            // `pageType=searchDestinations` (city-card links, bookmarks) is only corrected by PageWrapperClient
            // after the searchParams handed in here were read.
            initBiffSearchPage({
              configuration: BIFF_CONFIGURATIONS.RESULTS,
              destination: searchInfoDetailData?.destination?.cityName,
              isLoggedIn: !!authResponse?.loggedIn,
              fromIndex: platformDataIndex,
            });
          });
        }
      }

      if (apiStatus === APIStatusEnum.OVERFILTERED) {
        starTrack.resultPageFilterNoResult(createResultPageFilterNoResultPayload({ searchId, localization }));
      }
    } else if (apiStatus !== APIStatusEnum.WAIT && apiStatus !== APIStatusEnum.OVERFILTERED) {
      updateAnalyticsViewItemListEvent({
        searchParams,
        localization,
        dealCards: productsData?.dealCards,
      });
    }

    if (apiStatus !== APIStatusEnum.WAIT && apiStatus !== APIStatusEnum.OVERFILTERED) {
      const pageType = getPageTypeFromSearchParams(searchParams);

      if (hasOutsideDestinations({ productsData })) {
        starTrack.mapOutsideDestinationHotelsRetrieved(
          createMapOutsideDestinationHotelsRetrievedPayload({ searchParams, productsData })
        );
      }

      // Only fire RESULT_SHOWN for actual search results pages, not detail pages
      if (pageType === PAGE_TYPE.SEARCH_DESTINATIONS || pageType === PAGE_TYPE.SEARCH) {
        trackingResultShown({
          starTrack,
          searchParams,
          searchInfoData,
          listView,
          productsData,
          commonData,
          localization,
        });
      }
    }
  } catch (e) {
    console.log('Error calling starTrack.searchRequested', e, { searchParams });
  }
};

type FireSearchDestintionsEventsAfterProps = {
  searchParams: SearchParams;
  productsData: ProductsData;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  defaultDestItems: DefaultDestItems[];
  commonData: CommonData;
  localization: Localization;
  curiosityParams: CuriosityParams;
  starTrack: StarTrack;
};
export const fireSearchDestinationsEventsAfter = ({
  searchParams,
  productsData,
  searchInfoData,
  searchInfoDetailData,
  defaultDestItems,
  commonData,
  localization,
  starTrack,
}: FireSearchDestintionsEventsAfterProps) => {
  try {
    if (productsData) {
      if (shouldTrackAdditionalParams(searchParams)) {
        starTrack.searchAdditionalParameter(
          createSearchAdditionalParameterPayload({ searchId: searchInfoData.searchId, searchParams })
        );
      }

      starTrack.searchServed(
        createSearchServedPayload({
          searchId: searchInfoData.searchId,
          responseDetail: productsData.message,
          productCounters: productsData.productCounters,
          numberOfDealCards: productsData.cityCards?.length || 0,
          defaultSortOrder: searchParams[URLParameters.SORT],
          vcSearchId: productsData.vcSearchId,
          instantSearch: productsData.instantSearch,
          apexAvailabilityFlag: productsData.apexAvailabilityFlag,
        })
      );

      const pageType = getPageTypeFromSearchParams(searchParams);

      // Products search is also triggered in deeplinks to hotel detail pages.
      // In that case we don't want to track analytics results event and Biff is initialized in hotel detail page.
      if (pageType === PAGE_TYPE.SEARCH_DESTINATIONS || pageType === PAGE_TYPE.SEARCH) {
        analyticsResultsEvent({
          localization,
          totalNumberOfResults: productsData.productCounters?.productsCount || null,
          searchId: searchInfoData.searchId,
          defaultDestItems,
          isSearch: true,
          searchInfoData,
          searchInfoDetailData,
          commonData,
        }).then(() => {
          analyticsViewItemListEvent({
            searchParams,
            localization,
            dealCards: productsData?.dealCards,
          });
          // No biff here: the destinations page installs its MIDDLE_PAGE configuration from indexClient.
        });
      }
    }
  } catch (e) {
    console.log('Error calling starTrack.searchRequested', e, { searchParams });
  }
};
